// Developer: Ankit Gupta
// Shared source-system definitions used by project settings and the entity library.
import i18n from '@/i18n';

export interface SourceSystemOption {
  value: string;
  label: string;
}

// Product names stay in Latin script in both languages; the few labels with
// words in them are looked up when read, so every screen that lists these
// shows them in the reader's language without changing how it uses them.
const ENGLISH: [string, string][] = [
  ['R12', 'Oracle E-Business Suite (R12)'],
  ['11I', 'Oracle E-Business Suite (11i)'],
  ['FUSION', 'Oracle Fusion Cloud'],
  ['PSFT_R8', 'PeopleSoft R8'],
  ['PSFT_R9', 'PeopleSoft R9'],
  ['JDE', 'JD Edwards'],
  ['ATP', 'Oracle ATP (Cloud Wallet)'],
  ['POSTGRES', 'PostgreSQL'],
  ['MSSQL', 'Microsoft SQL Server'],
  ['CAD', 'CAD Drawing'],
  ['OTHER', 'Other / Generic Database'],
];

const labelOf = (code: string, english: string): string =>
  i18n.t(`common:sourceSystems.${code}`, { defaultValue: english });

/** All selectable source systems for a project. */
export const SOURCE_SYSTEMS: SourceSystemOption[] = ENGLISH.map(([value, english]) => ({
  value,
  get label() {
    return labelOf(value, english);
  },
}));

/** Source system whose projects use the API-source + CAD-inbox ingestion flow. */
export const CAD_SOURCE_SYSTEM = 'CAD';

/** Map of source-system code -> display label (derived from SOURCE_SYSTEMS). */
export const SOURCE_SYSTEM_LABELS: Record<string, string> = Object.defineProperties(
  {} as Record<string, string>,
  Object.fromEntries(
    ENGLISH.map(([code, english]) => [code, { enumerable: true, get: () => labelOf(code, english) }]),
  ),
);

/** Entity columns that hold a per-source-system base extraction SQL. */
export type DefaultSqlField =
  | 'default_sql_r12'
  | 'default_sql_11i'
  | 'default_sql_fusion'
  | 'default_sql_psft_r8'
  | 'default_sql_psft_r9'
  | 'default_sql_jde';

/** Any entity column that holds editable SQL. */
export type EntitySqlField = DefaultSqlField | 'csv_query' | 'summary_query';

/**
 * Source systems that have a dedicated base-extraction SQL column on the entity,
 * mapping each to its `default_sql_*` field and its `source_systems_supported` key.
 */
export const ENTITY_SQL_SOURCES: {
  key: string;
  label: string;
  field: DefaultSqlField;
}[] = [
  { key: 'R12', label: 'Oracle E-Business Suite (R12)', field: 'default_sql_r12' },
  { key: '11I', label: 'Oracle E-Business Suite (11i)', field: 'default_sql_11i' },
  { key: 'FUSION', label: 'Oracle Fusion Cloud', field: 'default_sql_fusion' },
  { key: 'PSFT_R8', label: 'PeopleSoft R8', field: 'default_sql_psft_r8' },
  { key: 'PSFT_R9', label: 'PeopleSoft R9', field: 'default_sql_psft_r9' },
  { key: 'JDE', label: 'JD Edwards', field: 'default_sql_jde' },
];
